import { percent, REGISTER_AXES, scenarioLabel } from '../../lib/voice'
import type { VoiceProfileView } from '../../types/voice'
import { RegisterRadar } from './RegisterRadar'

// The voice at a glance: the overall register on the radar, the prose reading of it, the four
// numbers that matter, and how the person opens and closes. Nothing here is editable; the
// scenario detail below is where the person corrects the record.
export function Glance({ profile, onPick }: { profile: VoiceProfileView; onPick: (scenario: string) => void }) {
  const d = profile.overall.descriptor
  const checked = profile.scenarios.filter((s) => s.selfCheckScore != null)
  const selfCheck = checked.length ? checked.reduce((n, s) => n + (s.selfCheckScore ?? 0), 0) / checked.length : profile.overall.selfCheckScore
  const described = profile.scenarios.filter((s) => !s.descriptor.provisional).length
  const strongest = REGISTER_AXES.map((a) => ({ a, v: d.register[a.key] })).sort((x, y) => Math.abs(y.v - 0.5) - Math.abs(x.v - 0.5)).slice(0, 2)
  const busiest = [...profile.scenarios].sort((a, b) => b.messageCount - a.messageCount).slice(0, 4)

  return (
    <section className="glance">
      <div className="glance-radar">
        <RegisterRadar register={d.register} size={250} />
        <p className="glance-axes">
          {strongest.map(({ a, v }) => (
            <span key={a.key}><strong>{v >= 0.5 ? a.high : a.low}</strong> {percent(Math.abs(v - 0.5) * 2)}</span>
          ))}
        </p>
      </div>

      <div className="glance-words">
        <p className="kicker">Your voice, in words</p>
        {d.summary ? <p className="glance-summary">{d.summary}</p> : <p className="glance-summary muted">Not described yet. The measurements are real; a model turns them into words on the next read.</p>}
        <dl className="glance-conventions">
          <dt>Opens</dt>
          <dd>{d.greetings.slice(0, 3).map((g) => <span key={g.text} className="chip"><em>{g.text}</em> {percent(g.rate)}</span>)}{d.greetings.length === 0 && <span className="muted">no greeting, mostly</span>}</dd>
          <dt>Closes</dt>
          <dd>{d.closings.slice(0, 3).map((c) => <span key={c.text} className="chip"><em>{c.text}</em> {percent(c.rate)}</span>)}{d.closings.length === 0 && <span className="muted">no sign-off, mostly</span>}</dd>
          {d.neverSay.length > 0 && (
            <>
              <dt>Never</dt>
              <dd>{d.neverSay.slice(0, 6).map((n) => <span key={n} className="chip never">{n}</span>)}{d.neverSay.length > 6 && <span className="muted">+{d.neverSay.length - 6}</span>}</dd>
            </>
          )}
        </dl>
      </div>

      <div className="glance-stats">
        <div className="glance-stat">
          <strong>{profile.messageCount.toLocaleString()}</strong>
          <span>messages measured</span>
        </div>
        <div className="glance-stat">
          <strong>{profile.scenarios.length}</strong>
          <span>scenarios · {described} described</span>
        </div>
        <div className="glance-stat">
          <strong>{Math.round(d.length.medianWords)}</strong>
          <span>words, typical message</span>
        </div>
        <div className="glance-stat score-stat">
          {selfCheck != null ? (
            <span className="prof-score" style={{ ['--score' as string]: Math.max(0, Math.min(1, selfCheck)) }}><strong>{Math.round(selfCheck * 100)}</strong></span>
          ) : <strong>—</strong>}
          <span>self-check, your own held-out mail</span>
        </div>
        <div className="glance-busiest">
          <p className="kicker">Where you write most</p>
          <ul>
            {busiest.map((s) => (
              <li key={s.scenario}>
                <button className="link-button" onClick={() => onPick(s.scenario)}>{scenarioLabel(s.scenario).replace(/^Email · /, '')}</button>
                <span className="muted">{s.messageCount.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
